import { Injectable } from '@angular/core';

/** Number of `--esp-chart-N` series tokens declared in `_esp-tokens.scss`. */
export const ESP_CHART_SERIES_TOKEN_COUNT = 30;

/**
 * Reads CSS custom properties (SOL / Lyra / ESP design tokens) at runtime, for
 * consumers that cannot use `var(...)` themselves - chiefly ECharts, which
 * paints on a canvas and needs concrete colour strings.
 *
 * Tokens are read from `document.body`: the Lyra values of `--sol-*` and the
 * `--esp-*` tokens are declared on `[data-theme="lyra"]` (= <body>), not on
 * `:root`.
 */
@Injectable({ providedIn: 'root' })
export class ThemeTokenService {

  private static readonly VAR_REF = /var\(\s*(--[\w-]+)\s*(?:,\s*([^()]*(?:\([^()]*\))*[^()]*))?\)/;
  private static readonly MAX_VAR_DEPTH = 10;

  private readonly colorCache = new Map<string, string>();

  /**
   * Raw value of a custom property on <body>, with any nested `var(--x)`
   * references resolved (getComputedStyle may hand back the specified value,
   * e.g. `var(--lyra-base-color-blue-500)`). Returns `fallback` when the token
   * is not defined.
   */
  read(name: string, fallback = ''): string {
    const value = this.resolveVars(this.rawValue(name), 0);
    return value || fallback;
  }

  /**
   * A token resolved to a concrete colour string the browser itself computed
   * (`rgb(r, g, b)` / `rgba(r, g, b, a)`), which ECharts/zrender can parse -
   * unlike the CSS Color 4 `rgb(0 0 0 / 80%)` syntax Lyra tokens are written in.
   */
  color(name: string, fallback = ''): string {
    const cached = this.colorCache.get(name);
    if (cached) return cached;

    if (!this.rawValue(name)) return fallback;
    const resolved = this.computeColor(`var(${name})`);
    if (!resolved) return fallback;
    this.colorCache.set(name, resolved);
    return resolved;
  }

  /** {@link color} with its alpha channel multiplied by `alpha` (0..1). */
  colorWithAlpha(name: string, alpha: number, fallback = ''): string {
    return ThemeTokenService.withAlpha(this.color(name, fallback), alpha);
  }

  /** The `--esp-chart-1..N` series palette, in order, as concrete colours. */
  chartPalette(count = ESP_CHART_SERIES_TOKEN_COUNT): string[] {
    const palette: string[] = [];
    for (let i = 1; i <= count; i++) {
      const value = this.color(`--esp-chart-${i}`);
      if (!value) break;
      palette.push(value);
    }
    return palette;
  }

  /** The computed font family of <body> (Lyra: `"Lyra Sans", sans-serif`). */
  fontFamily(fallback = 'sans-serif'): string {
    if (typeof document === 'undefined' || !document.body) return fallback;
    return getComputedStyle(document.body).fontFamily?.trim() || fallback;
  }

  /** Drops cached colours (e.g. after the theme attribute changes). */
  clearCache(): void {
    this.colorCache.clear();
  }

  /**
   * Applies an alpha multiplier to an `rgb()`/`rgba()` colour string. Strings
   * in any other format are returned unchanged.
   */
  static withAlpha(color: string, alpha: number): string {
    const match = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:\s*[,/]\s*([\d.]+)(%?))?\s*\)$/.exec((color || '').trim());
    if (!match) return color;
    const [, r, g, b, a, pct] = match;
    let base = a == null ? 1 : parseFloat(a);
    if (pct) base /= 100;
    const out = Math.round(base * alpha * 1000) / 1000;
    return `rgba(${r}, ${g}, ${b}, ${out})`;
  }

  private rawValue(name: string): string {
    if (typeof document === 'undefined' || !document.body) return '';
    return getComputedStyle(document.body).getPropertyValue(name).trim();
  }

  private resolveVars(value: string, depth: number): string {
    if (!value || depth > ThemeTokenService.MAX_VAR_DEPTH) return value;
    const match = ThemeTokenService.VAR_REF.exec(value);
    if (!match) return value;
    const [whole, ref, refFallback] = match;
    const replacement = this.rawValue(ref) || (refFallback ?? '').trim();
    return this.resolveVars(value.replace(whole, replacement), depth + 1);
  }

  // Let the browser resolve the full var() chain and normalise the colour.
  // The probe sits under <body> so `--sol-*` resolve to their Lyra values.
  private computeColor(cssValue: string): string {
    if (typeof document === 'undefined' || !document.body) return '';
    const probe = document.createElement('span');
    probe.style.display = 'none';
    // An unlikely sentinel colour on the parent tells an invalid/unset value
    // (which would otherwise silently inherit it) apart from a real one.
    const host = document.createElement('span');
    host.style.color = 'rebeccapurple';
    host.style.display = 'none';
    host.appendChild(probe);
    probe.style.color = cssValue;
    document.body.appendChild(host);
    try {
      const sentinel = getComputedStyle(host).color;
      const computed = getComputedStyle(probe).color;
      return computed && computed !== sentinel ? computed : '';
    } finally {
      host.remove();
    }
  }
}
